import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Alert, Button, Grid,
  Chip, Skeleton, Tabs, Tab, alpha, Table,
  TableBody, TableCell, TableHead, TableRow, CircularProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  AutoAwesome as ONeilIcon,
  Psychology as MinerviniIcon,
  ShowChart as WeinsteinIcon,
  Waves as TurtleIcon,
  LocalFireDepartment as LivermoreIcon,
  Handshake as ConsensusIcon,
  BarChart as BacktestIcon,
  SignalCellularAlt as SignalIcon,
  TrackChanges as AccuracyIcon,
  SwapVert as RRIcon,
  Visibility as WatchlistIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { legendaryAPI } from '../services/api';
import SignalTable from '../components/legendary/SignalTable';
import BacktestSummary from '../components/legendary/BacktestSummary';
import AccuracyHistory from '../components/legendary/AccuracyHistory';
import WatchlistPreSignal from '../components/legendary/WatchlistPreSignal';

const STRATEGIES = [
  {
    key: 'ONEIL', label: "O'Neil",
    color: '#3b82f6', gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    icon: <ONeilIcon />, path: '/legendary-strategies/oneil',
  },
  {
    key: 'MINERVINI', label: 'Minervini',
    color: '#8b5cf6', gradient: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
    icon: <MinerviniIcon />, path: '/legendary-strategies/minervini',
  },
  {
    key: 'WEINSTEIN', label: 'Weinstein',
    color: '#10b981', gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    icon: <WeinsteinIcon />, path: '/legendary-strategies/weinstein',
  },
  {
    key: 'TURTLE', label: 'Turtle',
    color: '#f97316', gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
    icon: <TurtleIcon />, path: '/legendary-strategies/turtle',
  },
  {
    key: 'LIVERMORE', label: 'Livermore',
    color: '#ef4444', gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    icon: <LivermoreIcon />, path: '/legendary-strategies/livermore',
  },
];

export default function LegendaryHub() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [strategyFilter, setStrategyFilter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [scanResults, setScanResults] = useState(null);
  const [consensus, setConsensus] = useState(null);
  const [consensusLoading, setConsensusLoading] = useState(false);

  const runScan = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const res = await legendaryAPI.scan();
      setScanResults(res.data);
    } catch (err) { setError(err.response?.data?.detail || err.message); }
    finally { setLoading(false); }
  }, []);

  const fetchConsensus = useCallback(async () => {
    try {
      setConsensusLoading(true);
      const res = await legendaryAPI.getConsensus();
      setConsensus(res.data);
    } catch (err) { console.error('Consensus error:', err); }
    finally { setConsensusLoading(false); }
  }, []);

  useEffect(() => { runScan(); fetchConsensus(); }, [fetchConsensus, runScan]);

  const allSignals = scanResults?.results || [];
  const getCount = (key) => allSignals.filter(r => r.strategy === key).length;
  const filteredSignals = strategyFilter ? allSignals.filter(r => r.strategy === strategyFilter) : allSignals;

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      {/* ── Header ─────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 1.5,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(102, 126, 234, 0.3)',
          }}>
            <TrophyIcon sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', letterSpacing: -0.3 }}>
                {t('legendary.hub.title')}
              </Typography>
              {!loading && scanResults && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: allSignals.length > 0 ? '#10b981' : '#94a3b8', boxShadow: allSignals.length > 0 ? '0 0 6px #10b981' : 'none' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>
                    {t('legendary.hub.signalCount', { count: allSignals.length })}
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>
              {t('legendary.hub.subtitle')}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon sx={{ fontSize: 16 }} />}
          onClick={runScan} disabled={loading}
          sx={{
            textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px',
            bgcolor: '#667eea', '&:hover': { bgcolor: '#5a5fe0' },
          }}
        >
          {loading ? t('legendary.hub.scanning') : t('legendary.hub.scanAll')}
        </Button>
      </Box>

      {/* ── Market Regime Banner ─────────────────────── */}
      {!loading && scanResults && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 2, mb: 2, px: 2.5, py: 1.2,
          borderRadius: 2,
          bgcolor: scanResults.regime === 'Bullish' ? 'rgba(16,185,129,0.04)'
            : scanResults.regime === 'Bearish' ? 'rgba(239,68,68,0.04)' : 'rgba(245,158,11,0.04)',
          border: '1px solid',
          borderColor: scanResults.regime === 'Bullish' ? 'rgba(16,185,129,0.12)'
            : scanResults.regime === 'Bearish' ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)',
        }}>
          <Box sx={{
            width: 8, height: 8, borderRadius: '50%',
            bgcolor: scanResults.regime === 'Bullish' ? '#10b981'
              : scanResults.regime === 'Bearish' ? '#ef4444' : '#f59e0b',
            boxShadow: `0 0 6px ${scanResults.regime === 'Bullish' ? 'rgba(16,185,129,0.4)'
              : scanResults.regime === 'Bearish' ? 'rgba(239,68,68,0.4)' : 'rgba(245,158,11,0.4)'}`,
          }} />
          <Typography variant="body2" fontWeight={700} sx={{
            color: scanResults.regime === 'Bullish' ? '#10b981'
              : scanResults.regime === 'Bearish' ? '#ef4444' : '#f59e0b',
          }}>
            {t('legendary.watchlist.regime.market')}: {t(`legendary.hub.regimeLabel.${scanResults.regime}`, scanResults.regime)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('legendary.watchlist.regime.breadth', { value: scanResults.market_breadth })}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {t('legendary.watchlist.regime.scanTime', { value: scanResults.scan_time_s })}
          </Typography>
        </Box>
      )}

      {/* ── Strategy Filter Pills ────────────────────── */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip
          label={`${t('legendary.hub.tabs.allSignals')} ${scanResults ? allSignals.length : ''}`}
          onClick={() => setStrategyFilter(null)}
          sx={{
            fontWeight: 700, fontSize: '0.82rem', height: 34, px: 0.5,
            bgcolor: !strategyFilter ? '#667eea' : 'transparent',
            color: !strategyFilter ? '#fff' : 'text.secondary',
            border: '1px solid',
            borderColor: !strategyFilter ? '#667eea' : 'divider',
            '&:hover': { bgcolor: !strategyFilter ? '#5a5fe0' : alpha('#667eea', 0.08) },
          }}
        />
        {STRATEGIES.map((s) => {
          const count = scanResults ? getCount(s.key) : null;
          const active = strategyFilter === s.key;
          const hasSignals = count > 0;
          return (
            <Chip
              key={s.key}
              icon={React.cloneElement(s.icon, { sx: { fontSize: '14px !important', color: active ? `${s.color} !important` : hasSignals ? `${s.color} !important` : 'inherit' } })}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span>{s.label}</span>
                  {count != null && (
                    <Box sx={{
                      minWidth: 18, height: 18, borderRadius: '9px', display: 'inline-flex',
                      alignItems: 'center', justifyContent: 'center', fontSize: '0.68rem', fontWeight: 800,
                      bgcolor: hasSignals ? alpha(s.color, active ? 0.2 : 0.12) : alpha('#94a3b8', 0.08),
                      color: hasSignals ? s.color : '#94a3b8',
                      px: 0.5,
                    }}>
                      {count}
                    </Box>
                  )}
                </Box>
              }
              onClick={() => setStrategyFilter(active ? null : s.key)}
              onDoubleClick={() => navigate(s.path)}
              sx={{
                fontWeight: 600, fontSize: '0.8rem', height: 34, px: 0.5,
                bgcolor: active ? alpha(s.color, 0.1) : 'transparent',
                color: active ? s.color : hasSignals ? 'text.primary' : 'text.disabled',
                border: '1px solid',
                borderColor: active ? s.color : hasSignals ? alpha(s.color, 0.25) : 'divider',
                '&:hover': { bgcolor: alpha(s.color, 0.08), borderColor: alpha(s.color, 0.4) },
                '& .MuiChip-icon': { ml: 0.5, mr: -0.3 },
              }}
            />
          );
        })}
      </Box>

      {/* ── Tabs ─────────────────────────────────────────── */}
      <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{
          minHeight: 40,
          '& .MuiTab-root': {
            minHeight: 40, py: 0.5, fontWeight: 600, fontSize: '0.82rem',
            textTransform: 'none', gap: 0.5, color: '#94a3b8',
            '&.Mui-selected': { color: '#334155' },
          },
          '& .MuiTabs-indicator': { height: 2, borderRadius: '2px 2px 0 0', bgcolor: '#667eea' },
        }}>
          <Tab icon={<SignalIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={t('legendary.hub.tabs.allSignals')} />
          <Tab icon={<ConsensusIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={t('legendary.hub.tabs.consensus')} />
          <Tab icon={<WatchlistIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={t('legendary.hub.tabs.watchlist')} />
          <Tab icon={<AccuracyIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={t('legendary.hub.tabs.accuracy')} />
          <Tab icon={<BacktestIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={t('legendary.hub.tabs.backtest')} />
        </Tabs>
      </Box>

{error && <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>{error}</Alert>}

      {/* ── Tab Content ──────────────────────────────────── */}
      {tab === 0 && (
        <Box>
          <SignalTable signals={filteredSignals} loading={loading} showStrategy />
        </Box>
      )}

      {tab === 1 && (
        <Box>
          {consensusLoading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {[0, 1].map((i) => (
                <Box key={i} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.2, borderBottom: '1px solid #f1f5f9' }}>
                    <Skeleton width={48} height={22} sx={{ borderRadius: 1 }} />
                    <Skeleton width={72} height={20} sx={{ borderRadius: 1.5 }} />
                    <Skeleton width={56} height={20} sx={{ borderRadius: 1 }} />
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 2 }}>
                      <Skeleton width={60} height={16} />
                    </Box>
                  </Box>
                  <Box sx={{ px: 2, py: 1 }}>
                    {[0, 1].map(r => (
                      <Box key={r} sx={{ display: 'flex', gap: 2, py: 0.8 }}>
                        <Skeleton width={70} height={14} />
                        <Skeleton width={40} height={14} />
                        <Skeleton width={50} height={14} />
                        <Skeleton width={50} height={14} />
                        <Skeleton width={40} height={14} />
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          {consensus?.items?.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {consensus.items.map((item, itemIdx) => {
                const matched = STRATEGIES.filter(s => item.strategies?.includes(s.key));
                const accentColor = matched[0]?.color || '#667eea';
                return (
                  <Box key={item.symbol} sx={{
                    borderRadius: 2, overflow: 'hidden',
                    border: '1px solid', borderColor: '#e5e7eb',
                    transition: 'border-color 0.2s',
                    '&:hover': { borderColor: alpha(accentColor, 0.35) },
                  }}>
                    {/* Header row */}
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
                      borderBottom: '1px solid #f1f5f9',
                      borderLeft: `3px solid ${accentColor}`,
                    }}>
                      <Typography fontWeight={800} sx={{ fontSize: '1rem' }}>
                        {item.symbol}
                      </Typography>
                      <Chip label={t('legendary.consensus.strategies', { count: item.count })} size="small" sx={{
                        fontWeight: 700, fontSize: '0.6rem', height: 18, borderRadius: 1,
                        bgcolor: alpha('#6366f1', 0.08), color: '#6366f1',
                      }} />
                      {item.best_strength === 'STRONG' && (
                        <Chip size="small" label={t('legendary.consensus.strong')} sx={{
                          height: 18, fontSize: '0.58rem', fontWeight: 700,
                          bgcolor: alpha('#10b981', 0.08), color: '#10b981', borderRadius: 1,
                        }} />
                      )}
                      <Box sx={{ display: 'flex', gap: 0.5, ml: 0.5 }}>
                        {matched.map(st => (
                          <Box key={st.key} sx={{
                            width: 6, height: 6, borderRadius: '50%', bgcolor: st.color,
                          }} />
                        ))}
                      </Box>
                      {item.max_score != null && (
                        <Typography variant="caption" sx={{
                          ml: 'auto', fontWeight: 800, fontFamily: 'monospace', fontSize: '0.82rem',
                          color: item.max_score >= 90 ? '#10b981' : item.max_score >= 80 ? '#3b82f6' : '#f59e0b',
                        }}>
                          {item.max_score}
                        </Typography>
                      )}
                      {item.avg_risk_reward != null && (
                        <Typography variant="caption" sx={{
                          fontWeight: 700, fontFamily: 'monospace', fontSize: '0.75rem',
                          color: item.avg_risk_reward >= 2 ? '#10b981' : '#64748b',
                        }}>
                          R:R {item.avg_risk_reward}
                        </Typography>
                      )}
                    </Box>

                      {/* Full-width detail table */}
                      <Box>
                          {item.signals?.length > 0 && (
                            <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
                              <TableHead>
                                <TableRow sx={{ '& th': {
                                  fontWeight: 700, fontSize: '0.58rem', color: '#94a3b8', py: 0.8,
                                  textTransform: 'uppercase', letterSpacing: '0.04em', bgcolor: '#fafbfd',
                                  borderBottom: '1px solid #f1f5f9',
                                } }}>
                                  <TableCell>{t('legendary.consensus.columns.strategy')}</TableCell>
                                  <TableCell align="center">{t('legendary.consensus.columns.score')}</TableCell>
                                  <TableCell align="center">{t('legendary.consensus.columns.strength')}</TableCell>
                                  <TableCell align="right">{t('legendary.consensus.columns.entry')}</TableCell>
                                  <TableCell align="right">{t('legendary.consensus.columns.stop')}</TableCell>
                                  <TableCell align="right">{t('legendary.consensus.columns.target')}</TableCell>
                                  <TableCell align="right">{t('legendary.consensus.columns.riskPct')}</TableCell>
                                  <TableCell align="center">{t('legendary.consensus.columns.rr')}</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {item.signals.map((sig, si) => {
                                  const stConfig = STRATEGIES.find(s => s.key === sig.strategy);
                                  const sColor = stConfig?.color || '#64748b';
                                  const rrColor = sig.risk_reward >= 2 ? '#10b981' : sig.risk_reward >= 1 ? '#3b82f6' : '#f59e0b';
                                  const strChip = sig.signal_strength === 'STRONG'
                                    ? { c: '#10b981', l: t('legendary.consensus.strong') }
                                    : { c: '#f59e0b', l: t('legendary.consensus.moderate') };
                                  return (
                                    <TableRow key={si} sx={{
                                      '& td': { fontSize: '0.78rem', py: 0.7, borderBottom: si === item.signals.length - 1 ? 'none' : '1px solid #f8fafc' },
                                      bgcolor: si % 2 === 0 ? 'transparent' : '#fafbfd',
                                    }}>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: sColor, flexShrink: 0 }} />
                                          <Typography variant="caption" fontWeight={600} sx={{ color: '#475569', fontSize: '0.75rem' }}>
                                            {stConfig?.label || sig.strategy}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell align="center">
                                        <Typography variant="caption" sx={{
                                          fontWeight: 800, fontFamily: 'monospace', fontSize: '0.78rem',
                                          color: sig.score >= 90 ? '#10b981' : sig.score >= 80 ? '#3b82f6' : '#f59e0b',
                                        }}>
                                          {sig.score}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="center">
                                        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem', color: strChip.c }}>
                                          {strChip.l}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontFamily: 'monospace', color: '#475569', fontWeight: 600 }}>
                                        {sig.entry?.toLocaleString() || '—'}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontFamily: 'monospace', color: '#ef4444', fontWeight: 600 }}>
                                        {sig.stop?.toLocaleString() || '—'}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontFamily: 'monospace', color: '#10b981', fontWeight: 600 }}>
                                        {sig.target?.toLocaleString() || '—'}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600,
                                        color: sig.risk_pct > 8 ? '#ef4444' : sig.risk_pct > 5 ? '#f59e0b' : '#10b981',
                                      }}>
                                        {sig.risk_pct != null ? `${sig.risk_pct}%` : '—'}
                                      </TableCell>
                                      <TableCell align="center">
                                        {sig.risk_reward != null && (
                                          <Box sx={{
                                            display: 'inline-flex', alignItems: 'center', gap: 0.3,
                                            px: 0.6, py: 0.15, borderRadius: 1, bgcolor: alpha(rrColor, 0.08),
                                          }}>
                                            <RRIcon sx={{ fontSize: 11, color: rrColor }} />
                                            <Typography variant="caption" sx={{ fontWeight: 700, fontFamily: 'monospace', color: rrColor, fontSize: '0.72rem' }}>
                                              {sig.risk_reward}
                                            </Typography>
                                          </Box>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          )}
                        </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            !consensusLoading && (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 5, textAlign: 'center' }}>
                <ConsensusIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#64748b', mb: 0.5 }}>
                  {t('legendary.consensus.empty')}
                </Typography>
                <Typography variant="body2" color="text.secondary" dangerouslySetInnerHTML={{ __html: t('legendary.consensus.emptyHint') }} />
              </Box>
            )
          )}
        </Box>
      )}

      {tab === 2 && (
        <WatchlistPreSignal />
      )}

      {tab === 3 && (
        <AccuracyHistory />
      )}

      {tab === 4 && (
        <Grid container spacing={2.5}>
          {STRATEGIES.map(s => (
            <Grid item xs={12} md={6} key={s.key}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <Box sx={{
                  width: 28, height: 28, borderRadius: 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: s.color, color: '#fff',
                }}>
                  {React.cloneElement(s.icon, { sx: { fontSize: 16 } })}
                </Box>
                <Typography variant="subtitle1" fontWeight={700}>{s.label}</Typography>
                <Typography variant="caption" color="text.secondary">{t(`legendary.strategies.${s.key}.subtitle`)}</Typography>
              </Box>
              <BacktestSummary strategy={s.key} color={s.color} />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
